import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import type { Template } from '../../types/template';
import { type Column, StandardTable } from '../common/UI/StandardTable';

interface TemplateListProps {
  templates: Template[];
  onDelete: (template: Template) => void;
  onDownload: (template: Template) => void;
  onSetDefault: (template: Template, scope: 'personal' | 'team' | 'company') => void;
  onPreview: (template: Template) => void;
  downloadingTemplateId: string | null;
  canManageClient?: boolean;
  canManageTeam?: boolean;
}

const TemplateList: React.FC<TemplateListProps> = ({
  templates,
  onDelete,
  onDownload,
  onSetDefault,
  onPreview,
  downloadingTemplateId,
  canManageClient = false,
  canManageTeam = false,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, template: Template) => {
    setAnchorEl(event.currentTarget);
    setSelectedTemplate(template);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedTemplate(null);
  };

  const handleAction = (scope: 'personal' | 'team' | 'company') => {
    if (selectedTemplate) {
      onSetDefault(selectedTemplate, scope);
    }
    handleCloseMenu();
  };

  const columns: Column<Template>[] = [
    {
      id: 'name',
      label: <span style={{ fontSize: '1rem', fontWeight: 'bold' }}>Name</span>,
      render: (template) => (
        <>
          <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
            {template.name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            {template.isDefault && (
              <Chip
                label="My Default"
                icon={<PersonIcon />}
                variant="filled"
                sx={{
                  height: 24,
                  fontSize: '0.75rem',
                  backgroundColor: theme.palette.common.black,
                  color: theme.palette.common.white,
                  '& .MuiChip-icon': {
                    color: theme.palette.common.white,
                    fontSize: '1rem',
                  },
                }}
              />
            )}
            {template.isTeamDefault && (
              <Chip
                label="Team Default"
                icon={<GroupsIcon />}
                variant="filled"
                sx={{
                  height: 24,
                  fontSize: '0.75rem',
                  backgroundColor: theme.palette.bidlogiq.taupe,
                  color: theme.palette.common.black,
                  '& .MuiChip-icon': {
                    color: theme.palette.common.black,
                    fontSize: '1rem',
                  },
                }}
              />
            )}
            {template.isClientDefault && (
              <Chip
                label="Company Default"
                icon={<StarIcon />}
                variant="filled"
                sx={{
                  height: 24,
                  fontSize: '0.75rem',
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.common.white,
                  '& .MuiChip-icon': {
                    color: theme.palette.common.white,
                    fontSize: '1rem',
                  },
                }}
              />
            )}
          </Box>
        </>
      ),
    },
    {
      id: 'dateUploaded',
      label: <span style={{ fontSize: '1rem', fontWeight: 'bold' }}>Date Uploaded</span>,
      render: (template) =>
        template.createdAt instanceof Date
          ? template.createdAt.toLocaleDateString()
          : new Date(
              (template.createdAt as { seconds: number }).seconds * 1000
            ).toLocaleDateString(),
    },
    {
      id: 'visibility',
      label: <span style={{ fontSize: '1rem', fontWeight: 'bold' }}>Visibility</span>,
      render: (template) =>
        template.scope === 'company' ? (
          <Tooltip title="Company templates are available to all users in your organization.">
            <Chip
              label="Company"
              variant="outlined"
              color="primary"
              sx={{ fontSize: '0.875rem', height: 28 }}
            />
          </Tooltip>
        ) : (
          <Tooltip title="Personal templates are only visible to you.">
            <Chip
              label="Personal"
              variant="outlined"
              color="default"
              sx={{ fontSize: '0.875rem', height: 28 }}
            />
          </Tooltip>
        ),
    },
    {
      id: 'status',
      label: <span style={{ fontSize: '1rem', fontWeight: 'bold' }}>Status</span>,
      render: (template) => (
        <Tooltip title={`Found tags: ${template.validTagsFound.join(', ')}`}>
          <Chip
            label="Verified"
            color="success"
            variant="outlined"
            sx={{ height: 28, fontSize: '0.875rem' }}
          />
        </Tooltip>
      ),
    },
    {
      id: 'actions',
      label: <span style={{ fontSize: '1rem', fontWeight: 'bold' }}>Actions</span>,
      align: 'right',
      render: (template) => (
        <>
          <Tooltip title="Set Default">
            <span>
              <IconButton
                onClick={(e) => handleOpenMenu(e, template)}
                color={
                  template.isDefault || template.isTeamDefault || template.isClientDefault
                    ? 'primary'
                    : 'default'
                }
              >
                {template.isDefault || template.isTeamDefault || template.isClientDefault ? (
                  <StarIcon />
                ) : (
                  <StarBorderIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Preview">
            <IconButton aria-label="Preview" onClick={() => onPreview(template)}>
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download">
            <span>
              <IconButton
                aria-label="Download"
                onClick={() => onDownload(template)}
                disabled={downloadingTemplateId === template.id}
              >
                {downloadingTemplateId === template.id ? (
                  <CircularProgress size={24} />
                ) : (
                  <DownloadIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton aria-label="Delete" onClick={() => onDelete(template)} color="error">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <>
      <StandardTable
        columns={columns}
        data={templates}
        keyExtractor={(template) => template.id}
        emptyStateMessage="No templates found. Upload one to get started."
      />

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
        <MenuItem onClick={() => handleAction('personal')} disabled={!!selectedTemplate?.isDefault}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Set as My Default</ListItemText>
        </MenuItem>
        {canManageTeam && (
          <MenuItem
            onClick={() => handleAction('team')}
            disabled={!!selectedTemplate?.isTeamDefault}
          >
            <ListItemIcon>
              <GroupsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Set as Team Default</ListItemText>
          </MenuItem>
        )}
        {canManageClient && selectedTemplate?.scope === 'company' && (
          <MenuItem
            onClick={() => handleAction('company')}
            disabled={!!selectedTemplate?.isClientDefault}
          >
            <ListItemIcon>
              <StarIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Set as Company Default</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default TemplateList;
